<template lang="pug">
  .module__main
    .module__title Настройки
    .module-content__container(v-loading="isLoading")
      .settings__container
        .setting__container
          .setting__title Общие настройки
          .setting__block
            .setting-block__title Комиссия
            el-input(v-model="fee" type="number")
            el-button(@click="updateFee()" type="success" size="mini") Изменить
        .setting__container
          .setting__title AML
          .setting__block
            .setting-block__title Минимальный баланс адреса для AML-проверки, BTC
            el-input(v-model="aml_min_balance" type="number" min="0" step="0.00000001")
            el-button(@click="updateAmlMinBalance()" type="success" size="mini") Изменить
        .setting__container.wide
          .setting__title Доверенные кошельки
          .setting__block
            .wallet-address__container(v-for="wallet in whitelist_wallets" :key="`whitelist-wallet-${wallet}`")
              span.wallet-address_text(:title="wallet") {{ wallet }}
              el-button.wallet-address__btn(
                size="mini"
                circle
                type="danger"
                icon="el-icon-delete"
                @click="requestDeleteWhitelistAddress(wallet)"
              )
            .wallet-address__container
              el-input(v-model="new_whitelist_wallet")
              el-button.wallet-address__btn(
                size="mini"
                circle
                type="primary"
                icon="el-icon-plus"
                :disabled="!new_whitelist_wallet"
                @click="requestAddWhitelistAddress()"
              )
        .setting__container.wide
          .setting__title Накопительные адреса
          .setting__block.accumulation-settings
            .accumulation-setting
              .setting-block__title Порог оборота для повторной выдачи, BTC
              el-input(v-model="accumulation_max_turnover" type="number" min="0" step="0.00000001")
            .accumulation-setting
              .setting-block__title Максимальный AML
              el-input-number(v-model="accumulation_max_aml_score" :min="1" :max="100" :step="1")
            .accumulation-setting
              .setting-block__title Ожидание, минут
              el-input-number(v-model="accumulation_wait_timeout_minutes" :min="1" :max="maxAccumulationWaitTimeoutMinutes" :step="1")
            el-button(type="success" size="mini" @click="updateAccumulationSettings") Сохранить
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
  ADD_WHITELIST_WALLET,
  DELETE_WHITELIST_WALLET,
  LOAD_SETTINGS,
  UPDATE_ACCUMULATION_SETTINGS,
  UPDATE_SETTING,
} from '@/store/const/settings';
import instruments from '@/mixins/instruments';

export default {
  name: 'Settings',
  mixins: [instruments],
  data() {
    return {
      fee: 0,
      whitelist_wallets: [],
      new_whitelist_wallet: '',
      aml_min_balance: 0,
      accumulation_max_turnover: '',
      accumulation_max_aml_score: 50,
      accumulation_wait_timeout_minutes: 60,
      maxAccumulationWaitTimeoutMinutes: 7 * 24 * 60,
    };
  },
  async mounted() {
    await this.loadAndSetSettings();
  },
  computed: {
    ...mapGetters(['loading', 'error']),
    ...mapGetters('settings', ['cryptoType']),
    isLoading() {
      return this.loading(LOAD_SETTINGS) === 'loading';
    },
    isError() {
      return this.loading(LOAD_SETTINGS) === 'error';
    },
    errorMessage() {
      return this.error(LOAD_SETTINGS);
    },
  },
  methods: {
    ...mapActions('settings', {
      loadSettings: LOAD_SETTINGS,
      updateSetting: UPDATE_SETTING,
      addWhitelistWallet: ADD_WHITELIST_WALLET,
      deleteWhitelistWallet: DELETE_WHITELIST_WALLET,
      updateAccumulationConfig: UPDATE_ACCUMULATION_SETTINGS,
    }),
    async loadAndSetSettings() {
      const request = await this.loadSettings();
      if (request.success) {
        const settings = request.data;
        for (const settingName of Object.keys(settings)) {
          this[settingName] = settings[settingName];
        }
      }
    },
    async updateFee() {
      const fee =
        typeof this.fee === 'number' ? this.fee : parseFloat(this.fee);
      if (!Number.isFinite(fee) || fee <= 0) {
        return this.$message.warning(
          'Значение комиссии должно быть положительным',
        );
      }
      this.$prompt('Введите код 2FA', 'Проверка безопасности', {
        confirmButtonText: 'Изменить',
        cancelButtonText: 'Отменить',
      })
        .then(async ({ value }) => {
          await this.requestSettingUpdate('fee', {
            value: fee,
            token_2fa: value,
          });
        })
        .catch(() => {
          this.$message.info('Изменение комиссии отменено');
        });
    },
    async updateAmlMinBalance() {
      const value = Number(this.aml_min_balance);
      const valueSats = Math.round(value * 100000000);
      if (
        !Number.isFinite(value) ||
        value < 0 ||
        !Number.isSafeInteger(valueSats) ||
        Math.abs(value - valueSats / 100000000) > 1e-12
      ) {
        return this.$message.warning(
          'Минимальный AML-баланс должен быть неотрицательным и задан с точностью до сатоши',
        );
      }
      await this.requestSettingUpdate('aml_min_balance', { value });
    },
    async requestAddWhitelistAddress() {
      this.$prompt('Введите код 2FA', 'Проверка безопасности', {
        confirmButtonText: 'Добавить',
        cancelButtonText: 'Отменить',
      })
        .then(async ({ value }) => {
          const isAddressValid =
            this.cryptoType === 'BTC'
              ? this.isValidBtcAddress(this.new_whitelist_wallet)
              : this.isValidLtcAddress(this.new_whitelist_wallet);

          if (isAddressValid) {
            const request = await this.addWhitelistWallet({
              address: this.new_whitelist_wallet,
              token_2fa: value,
            });
            await this.notifyRequestResult({
              request,
              successMessage: 'Доверенный кошелек успешно добавлен',
              methodOnSuccess: this.loadAndSetSettings,
            });
            if (request.success) {
              this.new_whitelist_wallet = '';
            }
          } else {
            this.$message.error('Указанный адрес кошелька не валиден');
          }
        })
        .catch(() => {
          this.$message({
            type: 'info',
            message: 'Добавление доверенного адреса отменено',
          });
        });
    },
    async requestDeleteWhitelistAddress(address) {
      this.$prompt(
        `Введите код 2FA, чтобы удалить адрес ${address} из доверенных`,
        'Проверка безопасности',
        {
          confirmButtonText: 'Удалить',
          cancelButtonText: 'Отмена',
        },
      )
        .then(async ({ value }) => {
          const request = await this.deleteWhitelistWallet({
            address,
            token_2fa: value,
          });
          await this.notifyRequestResult({
            request,
            successMessage: 'Доверенный кошелек успешно удален',
            methodOnSuccess: this.loadAndSetSettings,
          });
        })
        .catch(() => {
          this.$message.info('Удаление отменено');
        });
    },
    async requestSettingUpdate(name, value) {
      const request = await this.updateSetting({ name, data: value });
      await this.notifyRequestResult({
        request,
        successMessage: 'Настройки успешно обновлены',
        methodOnSuccess: this.loadAndSetSettings,
      });
    },
    async updateAccumulationSettings() {
      const maxTurnover = Number(this.accumulation_max_turnover);
      const maxAmlScore = Number(this.accumulation_max_aml_score);
      const waitTimeoutMinutes = Number(this.accumulation_wait_timeout_minutes);
      if (!Number.isFinite(maxTurnover) || maxTurnover <= 0) {
        return this.$message.warning(
          'Укажите положительный максимальный оборот',
        );
      }
      if (
        !Number.isFinite(maxAmlScore) ||
        maxAmlScore <= 0 ||
        maxAmlScore > 100
      ) {
        return this.$message.warning('AML-лимит должен быть от 1 до 100');
      }
      if (
        !Number.isInteger(waitTimeoutMinutes) ||
        waitTimeoutMinutes <= 0 ||
        waitTimeoutMinutes > this.maxAccumulationWaitTimeoutMinutes
      ) {
        return this.$message.warning(
          'Таймаут должен быть от 1 минуты до 7 дней',
        );
      }
      const request = await this.updateAccumulationConfig({
        maxTurnover,
        maxAmlScore,
        waitTimeoutMinutes,
      });
      await this.notifyRequestResult({
        request,
        successMessage: 'Настройки накопительных адресов сохранены',
        methodOnSuccess: this.loadAndSetSettings,
      });
    },
  },
};
</script>
<style scoped>
.settings__container {
  display: flex;
  flex-wrap: wrap;
  overflow-y: scroll;
}

.setting__container {
  width: 240px;
  padding: 10px;
  background-color: white;
  margin: 5px;
  border-radius: 5px;
  box-shadow: 1px 1px 1px #9f9f9f;
}

.setting__container.wide {
  width: 480px;
}

.setting__title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 10px;
}

.setting__block {
  display: flex;
  flex-wrap: wrap;
  padding: 5px;
  margin-bottom: 5px;
}

.setting-block__title {
  font-weight: 500;
  flex: 1;
}

.wallet-address__container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 5px auto;
}

.wallet-address_text {
  font-size: 12px;
  width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wallet-address__btn {
  margin-left: 5px;
}

.accumulation-settings,
.accumulation-setting {
  width: 100%;
}

.accumulation-setting {
  margin-bottom: 10px;
}

.accumulation-setting .el-input-number {
  width: 100%;
}
</style>
